// src/app/data/controllers/ChatController.ts
import { makeObservable, observable, action, runInAction } from 'mobx';
import { SingletonClass } from '@/app/utils/SingletonClass';
import { OmegaWsService } from '@/sdk/OmegaWsService';
import type { OmegaWsClient, AgentRun } from '@kabco/omega-ws';

type ChatMessageHandler = (response: any) => void;

export class ChatController extends SingletonClass {
    private static _instance: ChatController;
    public client: OmegaWsClient; // Делаем public для доступа к client.files

    state: string = 'idle';
    error: Error | null = null;
    wasEverConnected = false;

    activeConversationId: number | null = null;
    activeClientConversationId: string | null = null;

    private activeRun: AgentRun | null = null;
    private didBootstrapRuns = false;
    private messageHandlers: Set<ChatMessageHandler> = new Set();
    private cleanupFns: (() => void)[] = [];

    static getInstance(): ChatController {
        if (!ChatController._instance) {
            ChatController._instance = new ChatController();
        }
        return ChatController._instance;
    }

    constructor() {
        super();
        this.client = OmegaWsService.getInstance().getOrCreateClient();

        makeObservable(this, {
            state: observable,
            error: observable,
            wasEverConnected: observable,
            activeConversationId: observable,
            activeClientConversationId: observable,
            onMessage: action,
            sendText: action,
            cancel: action,
            resume: action,
            dispose: action,
        });

        this.setupListeners();
    }

    private setupListeners(): void {
        const unsubStatus = this.client.on('status', (status: string) => {
            runInAction(() => {
                this.state = status;
                if (status === 'ready') {
                    this.wasEverConnected = true;
                    this.error = null;
                }
            });
            if (status === 'ready' && !this.didBootstrapRuns) {
                this.didBootstrapRuns = true;
                void this.bootstrapRecoverableRuns();
            }
        });

        const unsubError = this.client.on('error', (err: Error) => {
            runInAction(() => { this.error = err; });
        });

        const unsubNetwork = this.client.on('network', (online: boolean) => {
            this.messageHandlers.forEach(handler => handler({ type: 'network', online }));
        });

        const unsubReconnected = this.client.on('reconnected', (event: any) => {
            this.messageHandlers.forEach(handler => handler({ type: 'reconnected', event }));
        });

        const unsubUserCreated = this.client.events?.conversation?.onUserMessageCreated?.((event: any) => {
            this.messageHandlers.forEach(handler => handler(event));
        });
        const unsubAssistantStarted = this.client.events?.run?.onAssistantStarted?.((event: any) => {
            this.messageHandlers.forEach(handler => handler(event));
        });
        const unsubConversationUpdated = this.client.events?.conversation?.onUpdated?.((event: any) => {
            this.messageHandlers.forEach(handler => handler(event));
        });

        const unsubServerError = this.client.events?.error?.on?.((event: any) => {
            console.warn('[ChatController] server error', event?.code, event?.message);
            this.messageHandlers.forEach(handler => handler({
                type: 'serverError',
                code: event?.code,
                message: event?.message,
                requestId: event?.requestId,
            }));
        });

        this.cleanupFns.push(
            unsubStatus,
            unsubError,
            unsubNetwork,
            unsubReconnected,
            unsubUserCreated,
            unsubAssistantStarted,
            unsubConversationUpdated,
        );
        if (unsubServerError) this.cleanupFns.push(unsubServerError);
    }

    private async bootstrapRecoverableRuns(): Promise<void> {
        try {
            const result: any = await this.client.runs.list({ scope: 'ACTIVE', limit: 50 });
            const runs: any[] = Array.isArray(result?.runs) ? result.runs : [];
            if (runs.length === 0) return;
            this.messageHandlers.forEach(handler => handler({ type: 'runs.list', runs }));
        } catch (e: any) {
            console.warn('[ChatController] bootstrap runs.list failed', e?.code || e?.message || e);
        }
    }

    onMessage(handler: ChatMessageHandler): () => void {
        this.messageHandlers.add(handler);
        return () => { this.messageHandlers.delete(handler); };
    }

    async uploadFile(params: {
        file: File;
        fileName: string;
        conversationId?: number;
        clientConversationId?: string;
    }): Promise<any> {
        // SDK 0.7.1 сам делает всю магию с S3 и возвращает объект для agent.send attachments
        return this.client.files.upload(params);
    }

    sendText(
        content: string,
        conversationId?: number | null,
        clientConversationId?: string | null,
        model?: string,
        clientMessageId?: string,
        attachments?: any[]
    ): AgentRun {
        const payload: any = { content, model, attachments };

        if (conversationId != null) {
            payload.conversationId = conversationId;
        } else if (clientConversationId) {
            payload.clientConversationId = clientConversationId;
        } else {
            throw new Error('conversationId or clientConversationId is required');
        }

        if (clientMessageId) {
            payload.clientMessageId = clientMessageId;
        }

        const run = this.client.agent.send(payload);
        this.activeRun = run;
        return run;
    }

    retry(userMessageId: number, conversationId: number, model?: string): AgentRun {
        const payload: any = { userMessageId, conversationId, model };
        const run = this.client.agent.retry(payload);
        this.activeRun = run;
        return run;
    }

    async openRun(conversationId: number): Promise<AgentRun> {
        if (!Number.isFinite(conversationId) || conversationId <= 0) {
            throw new Error('[ChatController] invalid conversationId for openRun');
        }
        const run = await this.client.agent.open({ conversationId });
        this.activeRun = run;
        return run;
    }

    resume(payload: {
        action: 'CONFIRM' | 'REJECT' | 'ANSWER' | 'CONTINUE';
        toolCallId?: string;
        argumentsJson?: string;
        answer?: string;
        epoch?: number;
    }): void {
        if (!this.activeRun) {
            console.warn('[ChatController] resume: no active run');
            return;
        }
        try {
            this.activeRun.resume(payload);
        } catch (e: any) {
            console.warn('[ChatController] resume failed', e?.code || e?.message || e);
        }
    }

    cancel(requestId: string): void {
        try {
            this.client.agent.cancel(requestId);
        } catch (e: any) {
            console.warn('[ChatController] cancel failed', e?.message || e);
        }
    }

    dispose(): void {
        this.cleanupFns.forEach(fn => fn());
        this.cleanupFns = [];
        this.messageHandlers.clear();
        this.activeRun = null;
        this.didBootstrapRuns = false;
    }
}